import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check, X, Edit3, Star, StarOff, Eye, BarChart3, FileText, Users, Tag, Sparkles, ClipboardList } from 'lucide-react';
import StatCard from '../components/StatCard.tsx';
import TagPill from '../components/TagPill.tsx';
import InstagramGenerator from './InstagramGenerator.tsx';
import { ADMIN_STORIES, ADMIN_STATS, HEALTH_TAGS, type AdminStory } from '../data/mockData.ts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

type TabType = 'overview' | 'review' | 'featured' | 'tags' | 'applications' | 'generator';
type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

interface AuthUser {
  email: string;
  name: string;
  user_type: string;
}

const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [stories, setStories] = useState(ADMIN_STORIES);
  const [tags, setTags] = useState(HEALTH_TAGS);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<any[]>([]);
  const [appFilter, setAppFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [selectedStory, setSelectedStory] = useState<AdminStory | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      navigate('/signin', { replace: true });
      return;
    }

    try {
      const userData = JSON.parse(storedUser) as AuthUser;
      if (userData.user_type !== 'admin') {
        navigate('/', { replace: true });
        return;
      }
      setCurrentUser(userData);
    } catch {
      navigate('/signin', { replace: true });
      return;
    }
    setLoading(false);
  }, [navigate]);

  const isAdminEmail = currentUser?.email === 'curejournal@gmail.com';

  // Fetch applications when admin loads
  useEffect(() => {
    if (!isAdminEmail) return;
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/admin/applications`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setApplications(data))
      .catch(() => {});
  }, [isAdminEmail]);

  const handleAppStatus = async (appId: string, status: 'approved' | 'rejected') => {
    const token = localStorage.getItem('token');
    try {
      const resp = await fetch(`${API_URL}/api/admin/applications/${appId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (resp.ok) {
        setApplications((prev) =>
          prev.map((a) => (a.id === appId ? { ...a, status } : a))
        );
      }
    } catch {}
  };

  const filteredApplications = appFilter === 'all'
    ? applications
    : applications.filter((a) => a.status === appFilter);

  const handleApprove = (id: string) => {
    setStories((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: 'approved' as const } : s))
    );
  };

  const handleReject = (id: string) => {
    setStories((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: 'rejected' as const } : s))
    );
  };

  const handleToggleFeatured = (id: string) => {
    setStories((prev) =>
      prev.map((s) => (s.id === id ? { ...s, isFeatured: !s.isFeatured } : s))
    );
  };

  const filteredStories = statusFilter === 'all'
    ? stories
    : stories.filter((s) => s.status === statusFilter);

  const approvedStories = stories.filter((s) => s.status === 'approved');

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: <BarChart3 size={18} /> },
    { id: 'review' as TabType, label: 'Review Queue', icon: <FileText size={18} /> },
    { id: 'featured' as TabType, label: 'Featured', icon: <Star size={18} /> },
    { id: 'tags' as TabType, label: 'Tags', icon: <Tag size={18} /> },
    ...(isAdminEmail ? [{ id: 'applications' as TabType, label: 'Applications', icon: <ClipboardList size={18} /> }] : []),
    ...(isAdminEmail ? [{ id: 'generator' as TabType, label: 'Post Generator', icon: <Sparkles size={18} /> }] : []),
  ];

  if (loading) {
    return (
      <div className="admin-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--vs-text-secondary)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <h1>Admin Dashboard</h1>
            <p>Manage stories, reviews, and platform settings</p>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className="admin-tabs">
        <div className="container">
          <div className="tabs-bar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="admin-content">
        <div className="container">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="stats-grid">
                <StatCard
                  label="Total Submitted"
                  value={ADMIN_STATS.totalSubmitted}
                  icon={<FileText size={20} />}
                />
                <StatCard
                  label="Approved"
                  value={ADMIN_STATS.approved}
                  icon={<Check size={20} />}
                />
                <StatCard
                  label="Pending Review"
                  value={ADMIN_STATS.pending}
                  icon={<Users size={20} />}
                />
                <StatCard
                  label="Rejected"
                  value={ADMIN_STATS.rejected}
                  icon={<X size={20} />}
                />
              </div>
            </motion.div>
          )}

          {/* Review Queue Tab */}
          {activeTab === 'review' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="review-filters">
                {(['all', 'pending', 'approved', 'rejected'] as StatusFilter[]).map((status) => (
                  <button
                    key={status}
                    className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
                    onClick={() => setStatusFilter(status)}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                    <span className="filter-count">
                      {status === 'all'
                        ? stories.length
                        : stories.filter((s) => s.status === status).length}
                    </span>
                  </button>
                ))}
              </div>

              <div className="review-table">
                <div className="table-header">
                  <span className="col-title">Story</span>
                  <span className="col-author">Author</span>
                  <span className="col-status">Status</span>
                  <span className="col-date">Submitted</span>
                  <span className="col-actions">Actions</span>
                </div>
                {filteredStories.map((story) => (
                  <div key={story.id} className="table-row">
                    <div className="col-title">
                      <span className="story-title">{story.title}</span>
                      <div className="story-tags">
                        {story.tags.slice(0, 2).map((tag) => (
                          <TagPill key={tag} tag={tag} size="sm" />
                        ))}
                      </div>
                    </div>
                    <div className="col-author">
                      <span className="author-name">
                        {story.isAnonymous ? 'Anonymous' : story.authorName}
                      </span>
                      <span className="author-email">{story.authorEmail}</span>
                    </div>
                    <div className="col-status">
                      <span className={`status-badge status-${story.status}`}>
                        {story.status}
                      </span>
                    </div>
                    <div className="col-date">
                      {new Date(story.submittedAt).toLocaleDateString()}
                    </div>
                    <div className="col-actions">
                      <button 
                        className="action-btn action-view" 
                        title="View Story"
                        onClick={() => setSelectedStory(story)}
                      >
                        <Eye size={16} />
                      </button>
                      {story.status === 'pending' && (
                        <>
                          <button
                            className="action-btn action-approve"
                            title="Approve"
                            onClick={() => handleApprove(story.id)}
                          >
                            <Check size={16} />
                          </button>
                          <button
                            className="action-btn action-edit"
                            title="Request Edit"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            className="action-btn action-reject"
                            title="Reject"
                            onClick={() => handleReject(story.id)}
                          >
                            <X size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Featured Tab */}
          {activeTab === 'featured' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <p className="tab-description">
                Featured stories appear prominently on the homepage.
              </p>
              <div className="featured-list">
                {approvedStories.map((story) => (
                  <div key={story.id} className="featured-item">
                    <div className="featured-info">
                      <h3>{story.title}</h3>
                      <div className="featured-meta">
                        {story.isAnonymous ? 'Anonymous' : story.authorName}
                      </div>
                    </div>
                    <button
                      className={`feature-toggle ${story.isFeatured ? 'active' : ''}`}
                      onClick={() => handleToggleFeatured(story.id)}
                    >
                      {story.isFeatured ? <StarOff size={18} /> : <Star size={18} />}
                      {story.isFeatured ? 'Unfeature' : 'Feature'}
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Tags Tab */}
          {activeTab === 'tags' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <p className="tab-description">
                Manage health topic tags for story categorization.
              </p>
              <div className="tags-list">
                {tags.map((tag) => (
                  <div key={tag.id} className="tag-item">
                    <div className="tag-info">
                      <span className="tag-name">{tag.name}</span>
                      <span className="tag-count">{tag.count} stories</span>
                    </div>
                    <div className="tag-actions">
                      <button className="action-btn action-edit" title="Rename">
                        <Edit3 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn btn-secondary add-tag-btn">
                Add New Tag
              </button>
            </motion.div>
          )}

          {/* Applications Tab - Only for curejournal@gmail.com */}
          {activeTab === 'applications' && isAdminEmail && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              data-testid="applications-tab"
            >
              <div className="section-header-row">
                <h2>Review Board Applications</h2>
                <span className="app-count">{applications.filter(a => a.status === 'pending').length} pending</span>
              </div>

              <div className="status-filters">
                {(['all', 'pending', 'approved', 'rejected'] as const).map((f) => (
                  <button
                    key={f}
                    className={`filter-btn ${appFilter === f ? 'active' : ''}`}
                    onClick={() => setAppFilter(f)}
                    data-testid={`app-filter-${f}`}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                    {f !== 'all' && (
                      <span className="filter-count">
                        {applications.filter((a) => a.status === f).length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {filteredApplications.length === 0 ? (
                <div className="empty-state">
                  <p>No {appFilter === 'all' ? '' : appFilter} applications yet.</p>
                </div>
              ) : (
                <div className="applications-list">
                  {filteredApplications.map((app) => (
                    <div key={app.id} className="application-card" data-testid={`application-${app.id}`}>
                      <div className="application-header">
                        <div>
                          <h3 className="application-name">{app.name}</h3>
                          <p className="application-meta">
                            {app.university} &middot; {app.program} &middot; Year {app.year}
                          </p>
                          <p className="application-email">{app.email}</p>
                        </div>
                        <span className={`application-status status-${app.status}`}>
                          {app.status}
                        </span>
                      </div>
                      <div className="application-body">
                        <p>{app.why_join}</p>
                      </div>
                      <div className="application-footer">
                        <span className="application-date">
                          {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                        </span>
                        {app.status === 'pending' && (
                          <div className="application-actions">
                            <button
                              className="btn btn-sm btn-approve"
                              onClick={() => handleAppStatus(app.id, 'approved')}
                              data-testid={`approve-app-${app.id}`}
                            >
                              <Check size={16} /> Approve
                            </button>
                            <button
                              className="btn btn-sm btn-reject"
                              onClick={() => handleAppStatus(app.id, 'rejected')}
                              data-testid={`reject-app-${app.id}`}
                            >
                              <X size={16} /> Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Post Generator Tab - Only for curejournal@gmail.com */}
          {activeTab === 'generator' && isAdminEmail && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <InstagramGenerator />
            </motion.div>
          )}
        </div>
      </main>

      <style>{`
        .admin-page {
          min-height: 100vh;
          background: var(--vs-bg-subtle);
        }

        .admin-header {
          background: var(--vs-white);
          padding: var(--vs-space-8) 0;
          border-bottom: 1px solid var(--vs-border);
        }

        .admin-header h1 {
          font-size: 1.5rem;
          margin-bottom: var(--vs-space-1);
        }

        .admin-header p {
          font-size: 0.9375rem;
          color: var(--vs-text-secondary);
        }

        .admin-tabs {
          background: var(--vs-white);
          border-bottom: 1px solid var(--vs-border);
        }

        .tabs-bar {
          display: flex;
          gap: var(--vs-space-1);
          padding: var(--vs-space-3) 0;
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: var(--vs-space-2);
          padding: var(--vs-space-2) var(--vs-space-4);
          font-family: var(--vs-font);
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--vs-text-secondary);
          background: transparent;
          border: none;
          border-radius: var(--vs-radius-md);
          cursor: pointer;
          transition: all var(--vs-transition-fast);
        }

        .tab-btn:hover {
          background: var(--vs-bg-hover);
          color: var(--vs-text-primary);
        }

        .tab-btn.active {
          background: var(--vs-black);
          color: var(--vs-white);
        }

        .admin-content {
          padding: var(--vs-space-8) 0 var(--vs-space-16);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: var(--vs-space-4);
        }

        .tab-description {
          font-size: 0.9375rem;
          color: var(--vs-text-secondary);
          margin-bottom: var(--vs-space-6);
        }

        /* Review Table */
        .review-filters {
          display: flex;
          gap: var(--vs-space-2);
          margin-bottom: var(--vs-space-6);
        }

        .filter-btn {
          display: flex;
          align-items: center;
          gap: var(--vs-space-2);
          padding: var(--vs-space-2) var(--vs-space-4);
          font-family: var(--vs-font);
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--vs-text-secondary);
          background: var(--vs-white);
          border: 1px solid var(--vs-border);
          border-radius: var(--vs-radius-full);
          cursor: pointer;
          transition: all var(--vs-transition-fast);
        }

        .filter-btn:hover {
          border-color: var(--vs-border-hover);
        }

        .filter-btn.active {
          background: var(--vs-black);
          color: var(--vs-white);
          border-color: var(--vs-black);
        }

        .filter-count {
          font-size: 0.75rem;
          opacity: 0.7;
        }

        .review-table {
          background: var(--vs-white);
          border: 1px solid var(--vs-border);
          border-radius: var(--vs-radius-lg);
          overflow: hidden;
        }

        .table-header,
        .table-row {
          display: grid;
          grid-template-columns: 2fr 1.5fr 100px 100px 140px;
          gap: var(--vs-space-4);
          padding: var(--vs-space-4) var(--vs-space-5);
          align-items: center;
        }

        .table-header {
          background: var(--vs-bg-subtle);
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--vs-text-tertiary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .table-row {
          border-top: 1px solid var(--vs-border);
        }

        .table-row:hover {
          background: var(--vs-bg-hover);
        }

        .story-title {
          font-weight: 500;
          color: var(--vs-text-primary);
          display: block;
          margin-bottom: var(--vs-space-2);
        }

        .story-tags {
          display: flex;
          gap: var(--vs-space-1);
        }

        .author-name {
          display: block;
          font-weight: 500;
          color: var(--vs-text-primary);
          font-size: 0.875rem;
        }

        .author-email {
          font-size: 0.75rem;
          color: var(--vs-text-tertiary);
        }

        .status-badge {
          display: inline-block;
          padding: var(--vs-space-1) var(--vs-space-2);
          font-size: 0.75rem;
          font-weight: 500;
          border-radius: var(--vs-radius-sm);
          text-transform: capitalize;
        }

        .status-pending {
          background: rgba(17, 17, 17, 0.08);
          color: var(--vs-text-secondary);
        }

        .status-approved {
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
        }

        .status-rejected {
          background: rgba(255, 90, 95, 0.1);
          color: var(--vs-coral);
        }

        .col-date {
          font-size: 0.875rem;
          color: var(--vs-text-secondary);
        }

        .col-actions {
          display: flex;
          gap: var(--vs-space-1);
        }

        .action-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: 1px solid var(--vs-border);
          border-radius: var(--vs-radius-md);
          color: var(--vs-text-secondary);
          cursor: pointer;
          transition: all var(--vs-transition-fast);
        }

        .action-btn:hover {
          border-color: var(--vs-border-hover);
          color: var(--vs-text-primary);
        }

        .action-approve:hover {
          border-color: #16a34a;
          color: #16a34a;
          background: rgba(34, 197, 94, 0.1);
        }

        .action-reject:hover {
          border-color: var(--vs-coral);
          color: var(--vs-coral);
          background: rgba(255, 90, 95, 0.1);
        }

        /* Featured List */
        .featured-list {
          display: flex;
          flex-direction: column;
          gap: var(--vs-space-3);
        }

        .featured-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--vs-space-4) var(--vs-space-5);
          background: var(--vs-white);
          border: 1px solid var(--vs-border);
          border-radius: var(--vs-radius-lg);
        }

        .featured-info h3 {
          font-size: 0.9375rem;
          font-weight: 500;
          margin-bottom: var(--vs-space-1);
        }

        .featured-meta {
          font-size: 0.8125rem;
          color: var(--vs-text-tertiary);
        }

        .meta-separator {
          margin: 0 var(--vs-space-2);
        }

        .feature-toggle {
          display: flex;
          align-items: center;
          gap: var(--vs-space-2);
          padding: var(--vs-space-2) var(--vs-space-3);
          font-family: var(--vs-font);
          font-size: 0.8125rem;
          font-weight: 500;
          color: var(--vs-text-secondary);
          background: transparent;
          border: 1px solid var(--vs-border);
          border-radius: var(--vs-radius-md);
          cursor: pointer;
          transition: all var(--vs-transition-fast);
        }

        .feature-toggle:hover {
          border-color: var(--vs-border-hover);
        }

        .feature-toggle.active {
          background: rgba(255, 90, 95, 0.1);
          border-color: var(--vs-coral);
          color: var(--vs-coral);
        }

        /* Tags List */
        .tags-list {
          display: flex;
          flex-direction: column;
          gap: var(--vs-space-2);
          margin-bottom: var(--vs-space-6);
        }

        .tag-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--vs-space-3) var(--vs-space-4);
          background: var(--vs-white);
          border: 1px solid var(--vs-border);
          border-radius: var(--vs-radius-md);
        }

        .tag-name {
          font-weight: 500;
        }

        .tag-count {
          font-size: 0.8125rem;
          color: var(--vs-text-tertiary);
          margin-left: var(--vs-space-3);
        }

        .add-tag-btn {
          margin-top: var(--vs-space-4);
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .tabs-bar {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .review-table {
            overflow-x: auto;
          }

          .table-header,
          .table-row {
            min-width: 700px;
          }
        }

        /* Applications Tab */
        .section-header-row {
          display: flex;
          align-items: baseline;
          gap: var(--vs-space-3);
          margin-bottom: var(--vs-space-6);
        }

        .section-header-row h2 {
          font-size: 1.25rem;
          font-weight: 600;
        }

        .app-count {
          font-size: 0.875rem;
          color: var(--vs-coral);
          font-weight: 500;
        }

        .applications-list {
          display: flex;
          flex-direction: column;
          gap: var(--vs-space-4);
        }

        .application-card {
          background: var(--vs-white);
          border: 1px solid var(--vs-border);
          border-radius: var(--vs-radius-lg);
          padding: var(--vs-space-6);
        }

        .application-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--vs-space-4);
        }

        .application-name {
          font-size: 1.0625rem;
          font-weight: 600;
          margin-bottom: var(--vs-space-1);
        }

        .application-meta {
          font-size: 0.875rem;
          color: var(--vs-text-secondary);
        }

        .application-email {
          font-size: 0.8125rem;
          color: var(--vs-text-tertiary);
          margin-top: 2px;
        }

        .application-status {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: var(--vs-space-1) var(--vs-space-3);
          border-radius: var(--vs-radius-full);
        }

        .status-pending { background: #fef3c7; color: #92400e; }
        .status-approved { background: #d1fae5; color: #065f46; }
        .status-rejected { background: #fee2e2; color: #991b1b; }

        .application-body {
          font-size: 0.9375rem;
          line-height: 1.7;
          color: var(--vs-text-secondary);
          margin-bottom: var(--vs-space-4);
          border-top: 1px solid var(--vs-border);
          padding-top: var(--vs-space-4);
        }

        .application-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .application-date {
          font-size: 0.8125rem;
          color: var(--vs-text-tertiary);
        }

        .application-actions {
          display: flex;
          gap: var(--vs-space-2);
        }

        .btn-approve {
          display: inline-flex;
          align-items: center;
          gap: var(--vs-space-1);
          padding: var(--vs-space-2) var(--vs-space-4);
          background: #059669;
          color: white;
          border: none;
          border-radius: var(--vs-radius-md);
          font-size: 0.8125rem;
          font-weight: 500;
          cursor: pointer;
          transition: background var(--vs-transition-fast);
        }
        .btn-approve:hover { background: #047857; }

        .btn-reject {
          display: inline-flex;
          align-items: center;
          gap: var(--vs-space-1);
          padding: var(--vs-space-2) var(--vs-space-4);
          background: var(--vs-white);
          color: #dc2626;
          border: 1px solid #dc2626;
          border-radius: var(--vs-radius-md);
          font-size: 0.8125rem;
          font-weight: 500;
          cursor: pointer;
          transition: all var(--vs-transition-fast);
        }
        .btn-reject:hover { background: #fef2f2; }

        .empty-state {
          text-align: center;
          padding: var(--vs-space-12) 0;
          color: var(--vs-text-tertiary);
          font-size: 0.9375rem;
        }

        /* Story View Modal */
        .story-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
        }

        .story-modal {
          background: var(--vs-white);
          border-radius: var(--vs-radius-xl);
          width: 100%;
          max-width: 720px;
          max-height: 85vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .story-modal-header {
          padding: 1.5rem 1.5rem 1rem;
          border-bottom: 1px solid var(--vs-border);
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
        }

        .story-modal-header h2 {
          font-size: 1.375rem;
          font-weight: 600;
          margin: 0 0 0.5rem 0;
          line-height: 1.3;
        }

        .story-modal-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
          font-size: 0.875rem;
          color: var(--vs-text-secondary);
        }

        .story-modal-close {
          background: var(--vs-bg-subtle);
          border: none;
          border-radius: var(--vs-radius-md);
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: all 0.15s;
        }

        .story-modal-close:hover {
          background: var(--vs-bg-hover);
        }

        .story-modal-body {
          padding: 1.5rem;
          overflow-y: auto;
          flex: 1;
        }

        .story-modal-content {
          font-size: 1rem;
          line-height: 1.75;
          color: var(--vs-text-primary);
          white-space: pre-wrap;
        }

        .story-modal-content p {
          margin-bottom: 1.25rem;
        }

        .story-modal-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--vs-border);
        }

        .story-modal-footer {
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--vs-border);
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          background: var(--vs-bg-subtle);
        }

        .story-modal-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .story-modal-actions {
          display: flex;
          gap: 0.5rem;
        }

        .story-modal-actions .btn {
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          border-radius: var(--vs-radius-md);
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.375rem;
          transition: all 0.15s;
        }

        .story-modal-actions .btn-approve {
          background: #10b981;
          color: white;
          border: none;
        }

        .story-modal-actions .btn-approve:hover {
          background: #059669;
        }

        .story-modal-actions .btn-reject {
          background: white;
          color: #ef4444;
          border: 1px solid #fecaca;
        }

        .story-modal-actions .btn-reject:hover {
          background: #fef2f2;
        }

        .content-warning-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.5rem;
          background: #fef3c7;
          color: #92400e;
          border-radius: var(--vs-radius-sm);
          font-size: 0.75rem;
          font-weight: 500;
        }
      `}</style>

      {/* Story View Modal */}
      {selectedStory && (
        <div className="story-modal-overlay" onClick={() => setSelectedStory(null)}>
          <motion.div 
            className="story-modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="story-modal-header">
              <div>
                <h2>{selectedStory.title}</h2>
                <div className="story-modal-meta">
                  <span>{selectedStory.isAnonymous ? 'Anonymous' : selectedStory.authorName}</span>
                  <span>•</span>
                  <span>{selectedStory.authorEmail}</span>
                  {selectedStory.university && (
                    <>
                      <span>•</span>
                      <span>{selectedStory.university}</span>
                    </>
                  )}
                  {selectedStory.hasContentWarning && (
                    <span className="content-warning-badge">⚠️ Content Warning</span>
                  )}
                </div>
              </div>
              <button className="story-modal-close" onClick={() => setSelectedStory(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="story-modal-body">
              <div className="story-modal-content">
                {selectedStory.body.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
              
              {selectedStory.tags.length > 0 && (
                <div className="story-modal-tags">
                  {selectedStory.tags.map((tag) => (
                    <TagPill key={tag} tag={tag} size="sm" />
                  ))}
                </div>
              )}
            </div>

            <div className="story-modal-footer">
              <div className="story-modal-status">
                <span>Status:</span>
                <span className={`status-badge status-${selectedStory.status}`}>
                  {selectedStory.status}
                </span>
                <span>•</span>
                <span>Submitted {new Date(selectedStory.submittedAt).toLocaleDateString()}</span>
              </div>
              
              {selectedStory.status === 'pending' && (
                <div className="story-modal-actions">
                  <button 
                    className="btn btn-approve"
                    onClick={() => {
                      handleApprove(selectedStory.id);
                      setSelectedStory(null);
                    }}
                  >
                    <Check size={16} /> Approve
                  </button>
                  <button 
                    className="btn btn-reject"
                    onClick={() => {
                      handleReject(selectedStory.id);
                      setSelectedStory(null);
                    }}
                  >
                    <X size={16} /> Reject
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
